import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { WorkspaceAPI } from '../../services/api';

// Notification center + assistant state for the merchant workspace
// (docs/current/WORKSPACE_ASSISTANT_PLAN_2026-10-28.md). Pages register their help
// text here (PageHelp does it) so the assistant can explain the page on screen,
// the same way CopilotKit's useCopilotReadable hands app state to its copilot.

const FEED_REFRESH_MS = 2 * 60 * 1000;
const EMPTY_FEED = { items: [], unread_count: 0 };

const WorkspaceAssistantContext = createContext(null);

export const useWorkspaceAssistant = () => useContext(WorkspaceAssistantContext);

// Flatten PageHelp props into plain text for the assistant prompt.
export const pageHelpText = ({ title, description, steps = [] } = {}) => [
  title,
  description,
  ...steps.map((step) => [step.title, step.detail].filter(Boolean).join('：')),
].filter(Boolean).join('\n');

// Called by PageHelp; does nothing outside the merchant workspace layout.
export const useRegisterPageHelp = (help) => {
  const ctx = useContext(WorkspaceAssistantContext);
  const setPageHelp = ctx?.setPageHelp;
  const textValue = pageHelpText(help);
  useEffect(() => {
    if (!setPageHelp) return undefined;
    setPageHelp(textValue);
    return () => setPageHelp('');
  }, [setPageHelp, textValue]);
};

// mapLink rewrites a notification's /app/... link for a workspace with other routes (e-commerce).
export const WorkspaceAssistantProvider = ({ children, mapLink }) => {
  const [enabled, setEnabled] = useState(false);
  const [feed, setFeed] = useState(EMPTY_FEED);
  const [pageHelp, setPageHelp] = useState('');

  useEffect(() => {
    let live = true;
    // 401/403 (team members) and 404 (older backend) all mean "not here": stay hidden.
    Promise.resolve().then(() => WorkspaceAPI.config())
      .then((data) => { if (live) setEnabled(Boolean(data?.enabled)); })
      .catch(() => {});
    return () => { live = false; };
  }, []);

  const refresh = useCallback(() => {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return Promise.resolve();
    return Promise.resolve().then(() => WorkspaceAPI.notifications())
      .then((data) => setFeed({ items: data?.items || [], unread_count: Number(data?.unread_count) || 0 }))
      .catch((error) => {
        if ([401, 403, 404].includes(error?.response?.status)) setEnabled(false);
      });
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    refresh();
    const timer = setInterval(refresh, FEED_REFRESH_MS);
    window.addEventListener('focus', refresh);
    return () => {
      clearInterval(timer);
      window.removeEventListener('focus', refresh);
    };
  }, [enabled, refresh]);

  const markRead = useCallback(async (item) => {
    if (!item || item.kind !== 'stored' || item.read) return;
    setFeed((current) => ({
      items: current.items.map((entry) => (entry.id === item.id ? { ...entry, read: true } : entry)),
      unread_count: Math.max(0, current.unread_count - 1),
    }));
    await Promise.resolve().then(() => WorkspaceAPI.markRead(item.id)).catch(() => {});
  }, []);

  const markAllRead = useCallback(async () => {
    await Promise.resolve().then(() => WorkspaceAPI.markAllRead()).catch(() => {});
    await refresh();
  }, [refresh]);

  const value = useMemo(() => ({
    enabled, feed, refresh, markRead, markAllRead, pageHelp, setPageHelp, mapLink,
  }), [enabled, feed, refresh, markRead, markAllRead, pageHelp, mapLink]);

  return <WorkspaceAssistantContext.Provider value={value}>{children}</WorkspaceAssistantContext.Provider>;
};

export default WorkspaceAssistantContext;
